import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import {
  MessageCircle,
  Search,
  Filter,
  Clock,
  User,
  AlertCircle,
  CheckCircle,
  Users,
  RefreshCw,
  Eye,
} from 'lucide-react';
import { Grid, Card } from '../../styles/GlobalStyles';
import { StatsCard } from './StatsCard';

// Importar dados mockados
const chamadosData = require('../../data/fale-com-rh-data.json');

// Hook para detectar tamanho da tela
const useResponsive = () => {
  const [screenSize, setScreenSize] = useState({
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    width: 1024,
  });

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setScreenSize({
        isMobile: width <= 640,
        isTablet: width > 640 && width <= 1024,
        isDesktop: width > 1024,
        width,
      });
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  return screenSize;
};

// Styled Components
const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;
`;

const ContentWrapper = styled.div`
  padding: 0;
`;

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};

  svg {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const FilterContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 16px;
    border-radius: 8px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 12px;
  }
`;

const FilterHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const FilterTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 1rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-bottom: 1.5rem;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 14px;
  background-color: white;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all 0.2s ease;

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.muted};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 14px 16px 14px 44px;
    font-size: 16px; /* Evita zoom em iOS */
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.text.muted};
  pointer-events: none;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FilterLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748b;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background-color: white;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    border-color: #9ca3af;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 12px;
    font-size: 16px; /* Evita zoom em iOS */
  }
`;

const StatsGrid = styled(Grid)`
  margin-bottom: 2rem;
  gap: 1.5rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const ChamadosContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
`;

const ChamadosHeader = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  background-color: ${({ theme }) => theme.colors.surface};

  h2 {
    margin: 0;
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    color: ${({ theme }) => theme.colors.text.primary};
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.sm};

    svg {
      color: ${({ theme }) => theme.colors.primary};
    }
  }

  p {
    margin: ${({ theme }) => theme.spacing.xs} 0 0;
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const ChamadosTable = styled.div`
  overflow-x: auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: ${({ theme }) => theme.colors.surfaceLight};
`;

const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceLight};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 16px;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: 0.75rem;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};

  ${({ status, theme }) => {
    switch (status) {
      case 'NOVO':
        return `
          background-color: #3B82F6;
          color: white;
        `;
      case 'EM_ANDAMENTO':
        return `
          background-color: #F59E0B;
          color: white;
        `;
      case 'RESOLVIDO':
        return `
          background-color: #10B981;
          color: white;
        `;
      default:
        return `
          background-color: ${theme.colors.surfaceLight};
          color: ${theme.colors.text.secondary};
        `;
    }
  }}
`;

const PriorityBadge = styled.span<{ priority: string }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 0.75rem;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};

  ${({ priority, theme }) => {
    switch (priority) {
      case 'ALTA':
        return `
          background-color: #FEE2E2;
          color: #DC2626;
        `;
      case 'MEDIA':
        return `
          background-color: #FEF3C7;
          color: #D97706;
        `;
      case 'BAIXA':
        return `
          background-color: #DCFCE7;
          color: #16A34A;
        `;
      default:
        return `
          background-color: ${theme.colors.surfaceLight};
          color: ${theme.colors.text.secondary};
        `;
    }
  }}
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const UserAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  object-fit: cover;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;

  .name {
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  .email {
    font-size: 0.75rem;
    color: ${({ theme }) => theme.colors.text.muted};
  }
`;

const MobileCardContainer = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: block;
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

const MobileCard = styled.div`
  background-color: white;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.md};
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const MobileCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const MobileCardCode = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
`;

const MobileCardContent = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const MobileCardDescription = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const MobileCardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const MobileCardDate = styled.div`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const ActionsCell = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background-color: white;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    color: white;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

interface Chamado {
  id: string;
  codigo: string;
  descricao: string;
  categoria: string;
  usuario: {
    nome: string;
    email: string;
    avatar: string;
  };
  atendente: {
    nome: string;
    status: string;
  };
  status: string;
  prioridade: string;
  ultimaAtualizacao: string;
  criado: string;
  mensagens: any[];
}

interface FaleComRHProps {
  onChamadoClick?: (chamado: Chamado) => void;
}

export const FaleComRH: React.FC<FaleComRHProps> = ({ onChamadoClick }) => {
  const screenSize = useResponsive();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('Todos');
  const [selectedPriority, setSelectedPriority] = useState('Todos');
  const [selectedCategory, setSelectedCategory] = useState('Todos');

  const filteredChamados = useMemo(() => {
    return chamadosData.chamados.filter((chamado: Chamado) => {
      const matchesSearch =
        chamado.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        chamado.descricao.toLowerCase().includes(searchTerm.toLowerCase()) ||
        chamado.usuario.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        chamado.usuario.email.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        selectedStatus === 'Todos' || chamado.status === selectedStatus;
      const matchesPriority =
        selectedPriority === 'Todos' || chamado.prioridade === selectedPriority;
      const matchesCategory =
        selectedCategory === 'Todos' || chamado.categoria === selectedCategory;

      return (
        matchesSearch && matchesStatus && matchesPriority && matchesCategory
      );
    });
  }, [searchTerm, selectedStatus, selectedPriority, selectedCategory]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'NOVO':
        return <AlertCircle size={14} />;
      case 'EM_ANDAMENTO':
        return <RefreshCw size={14} />;
      case 'RESOLVIDO':
        return <CheckCircle size={14} />;
      default:
        return <Clock size={14} />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'NOVO':
        return 'Novo';
      case 'EM_ANDAMENTO':
        return 'Em Andamento';
      case 'RESOLVIDO':
        return 'Resolvido';
      default:
        return status;
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'ALTA':
        return 'Alta';
      case 'MEDIA':
        return 'Média';
      case 'BAIXA':
        return 'Baixa';
      default:
        return priority;
    }
  };

  const handleChamadoClick = (chamado: Chamado) => {
    if (onChamadoClick) {
      onChamadoClick(chamado);
    }
  };

  return (
    <Container>
      <ContentWrapper>
        <PageHeader>
          <PageTitle>
            <MessageCircle size={32} />
            Gerenciar Chamados
          </PageTitle>
        </PageHeader>

        {/* Estatísticas */}
        <StatsGrid columns={3} minColumnWidth="250px">
          <StatsCard
            title="Total de Chamados"
            value={filteredChamados.length}
            icon={<MessageCircle size={24} />}
            color="#1E40AF"
            bgColor="#1E40AF"
            iconColor="#FFFFFF"
          />
          <StatsCard
            title="Chamados Abertos"
            value={
              filteredChamados.filter((c: Chamado) => c.status === 'NOVO')
                .length
            }
            icon={<AlertCircle size={24} />}
            color="#DC2626"
            bgColor="#DC2626"
            iconColor="#FFFFFF"
          />
          <StatsCard
            title="Em Atendimento"
            value={
              filteredChamados.filter(
                (c: Chamado) => c.atendente.nome !== 'Não atribuído'
              ).length
            }
            icon={<Users size={24} />}
            color="#059669"
            bgColor="#059669"
            iconColor="#FFFFFF"
          />
        </StatsGrid>

        {/* Filtros */}
        <FilterContainer>
          <FilterHeader>
            <Filter size={24} color="#60A5FA" />
            <FilterTitle>Buscar e Filtrar Chamados</FilterTitle>
          </FilterHeader>

          <SearchContainer>
            <SearchIcon size={20} />
            <SearchInput
              type="text"
              placeholder="Buscar por código, usuário ou atendente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchContainer>

          <FilterGrid>
            <FilterGroup>
              <FilterLabel>Status</FilterLabel>
              <FilterSelect
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                {chamadosData.filtros.status.map((status: string) => (
                  <option key={status} value={status}>
                    {status === 'Todos'
                      ? 'Todos os Status'
                      : getStatusLabel(status)}
                  </option>
                ))}
              </FilterSelect>
            </FilterGroup>

            <FilterGroup>
              <FilterLabel>Prioridade</FilterLabel>
              <FilterSelect
                value={selectedPriority}
                onChange={(e) => setSelectedPriority(e.target.value)}
              >
                {chamadosData.filtros.prioridades.map((priority: string) => (
                  <option key={priority} value={priority}>
                    {priority === 'Todos'
                      ? 'Todas as Prioridades'
                      : getPriorityLabel(priority)}
                  </option>
                ))}
              </FilterSelect>
            </FilterGroup>

            <FilterGroup>
              <FilterLabel>Categoria</FilterLabel>
              <FilterSelect
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {chamadosData.filtros.categorias.map((category: string) => (
                  <option key={category} value={category}>
                    {category === 'Todos' ? 'Todas as Categorias' : category}
                  </option>
                ))}
              </FilterSelect>
            </FilterGroup>
          </FilterGrid>
        </FilterContainer>

        {/* Lista de Chamados */}
        <ChamadosContainer>
          <ChamadosHeader>
            <h2>
              <MessageCircle size={20} />
              Exibindo 1-{filteredChamados.length} de {filteredChamados.length}{' '}
              chamados
            </h2>
            <p>Clique em um chamado para abrir a conversa</p>
          </ChamadosHeader>

          {/* Tabela Desktop */}
          <ChamadosTable>
            <Table>
              <TableHeader>
                <tr>
                  <TableHeaderCell>Código</TableHeaderCell>
                  <TableHeaderCell>Descrição</TableHeaderCell>
                  <TableHeaderCell>Usuário</TableHeaderCell>
                  <TableHeaderCell>Atendente</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Prioridade</TableHeaderCell>
                  <TableHeaderCell>Última Atualização</TableHeaderCell>
                  <TableHeaderCell>Ações</TableHeaderCell>
                </tr>
              </TableHeader>
              <TableBody>
                {filteredChamados.map((chamado: Chamado) => (
                  <TableRow
                    key={chamado.id}
                    onClick={() => handleChamadoClick(chamado)}
                  >
                    <TableCell>
                      <strong>{chamado.codigo}</strong>
                    </TableCell>
                    <TableCell>{chamado.descricao}</TableCell>
                    <TableCell>
                      <UserInfo>
                        <UserAvatar
                          src={chamado.usuario.avatar}
                          alt={chamado.usuario.nome}
                        />
                        <UserDetails>
                          <div className="name">{chamado.usuario.nome}</div>
                          <div className="email">{chamado.usuario.email}</div>
                        </UserDetails>
                      </UserInfo>
                    </TableCell>
                    <TableCell>
                      <div>{chamado.atendente.nome}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                        {chamado.atendente.status}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={chamado.status}>
                        {getStatusIcon(chamado.status)}
                        {getStatusLabel(chamado.status)}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      <PriorityBadge priority={chamado.prioridade}>
                        {getPriorityLabel(chamado.prioridade)}
                      </PriorityBadge>
                    </TableCell>
                    <TableCell>
                      <div>{chamado.ultimaAtualizacao}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                        <Clock size={12} style={{ marginRight: '0.25rem' }} />
                        {chamado.criado}
                      </div>
                    </TableCell>
                    <TableCell>
                      <ActionsCell>
                        <ActionButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleChamadoClick(chamado);
                          }}
                          title="Ver conversa"
                        >
                          <Eye size={16} />
                        </ActionButton>
                      </ActionsCell>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ChamadosTable>

          {/* Cards Mobile */}
          <MobileCardContainer>
            {filteredChamados.map((chamado: Chamado) => (
              <MobileCard
                key={chamado.id}
                onClick={() => handleChamadoClick(chamado)}
              >
                <MobileCardHeader>
                  <MobileCardCode>{chamado.codigo}</MobileCardCode>
                  <StatusBadge status={chamado.status}>
                    {getStatusIcon(chamado.status)}
                    {getStatusLabel(chamado.status)}
                  </StatusBadge>
                </MobileCardHeader>

                <MobileCardContent>
                  <MobileCardDescription>
                    {chamado.descricao}
                  </MobileCardDescription>
                  <UserInfo>
                    <UserAvatar
                      src={chamado.usuario.avatar}
                      alt={chamado.usuario.nome}
                    />
                    <UserDetails>
                      <div className="name">{chamado.usuario.nome}</div>
                      <div className="email">{chamado.usuario.email}</div>
                    </UserDetails>
                  </UserInfo>
                </MobileCardContent>

                <MobileCardFooter>
                  <PriorityBadge priority={chamado.prioridade}>
                    {getPriorityLabel(chamado.prioridade)}
                  </PriorityBadge>
                  <MobileCardDate>
                    <Clock size={12} />
                    {chamado.ultimaAtualizacao}
                  </MobileCardDate>
                </MobileCardFooter>
              </MobileCard>
            ))}
          </MobileCardContainer>
        </ChamadosContainer>
      </ContentWrapper>
    </Container>
  );
};
